import { faBars, faSearch, faSignOut, faUser } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { jwtDecode } from 'jwt-decode'
import { Logo } from './SideMenu'
import { useMemo, useRef, useState } from 'react'
import { OverlayTrigger } from 'ui'
import { loadAccount } from '../features/baseSlice'
import { logout } from '../features/authSlice'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'

type Props = {
   onToggleMenu: () => void
   isMenuOpen: boolean
}

const Header = ({ onToggleMenu, isMenuOpen }: Props) => {
   const intl = useIntl()
   const dispatch = useAppDispatch()
   const auth = useAppSelector((state) => state.authToken)
   const profile = useAppSelector((state) => state.profile)
   const base = useAppSelector((state) => state.base)
   const [showProfileMenu, setShowProfileMenu] = useState(false)
   const [query, setQuery] = useState('')
   const inputSearch = useRef<HTMLInputElement>(null)
   const navigate = useNavigate()
   const { pathname } = useLocation()
   const pathSplited = pathname.split('/')
   const toggleProfileMenu = () => {
      setShowProfileMenu(!showProfileMenu)
      if (!showProfileMenu) {
         setTimeout(() => {
            inputSearch.current?.focus()
         }, 0)
      }
   }

   const filteredAccounts = useMemo(() => {
      const regex = new RegExp(query.trim(), 'i')
      const filteredItems = !query ? profile?.data?.accounts : profile?.data?.accounts.filter((i) => regex.test(i.name))
      return filteredItems
   }, [profile?.data?.accounts, query])

   // Si el perfil no cargó (p.ej. token rechazado) se muestra el usuario del token para que el menú con Logout siga visible
   const userName = useMemo(() => {
      if (profile.data?.name) {
         return profile.data.name
      }
      try {
         return jwtDecode(auth.token).sub || ''
      } catch {
         return ''
      }
   }, [profile.data?.name, auth.token])

   function onChange(event: React.ChangeEvent<HTMLInputElement>) {
      setQuery(event.target.value)
   }

   const logOut = () => {
      dispatch(logout())
   }
   const handleOnClick = (account: any) => {
      setShowProfileMenu(false)
      setQuery('')
      dispatch(loadAccount(account))
      if (pathSplited.length > 2) {
         navigate(`/${account.code}/${pathSplited[2]}`, { replace: true })
      } else {
         navigate(`/${account.code}/dashboard`)
      }
      window.location.reload()
   }
   return (
      <header className="fixed inset-x-0 top-0 z-50 flex h-16 items-center justify-between bg-amber-500 text-white lg:z-30">
         {auth && auth.isAuthenticated ? (
            <>
               <div className="flex items-center min-w-0 h-full">
                  <button
                     type="button"
                     className="lg:hidden ml-4 sm:ml-6 w-6 h-6"
                     onClick={onToggleMenu}
                     aria-label={intl.formatMessage({ id: 'titles.toggleMenu' })}
                     aria-expanded={isMenuOpen}
                  >
                     <FontAwesomeIcon icon={faBars} />
                  </button>
                  <div className="hidden lg:flex items-center h-full w-[17rem] shrink-0 px-6">
                     <Logo />
                  </div>
                  <span className="font-bold px-4 sm:px-6 lg:px-8">{base.account?.name || ''}</span>
               </div>
               <div className="relative h-full px-4 sm:px-6 lg:px-8">
                  <OverlayTrigger isOpen={showProfileMenu} onToggle={toggleProfileMenu}>
                     <OverlayTrigger.Trigger
                        componentTag="button"
                        className="flex items-center gap-x-2 focus:outline-none h-full"
                     >
                        <div className="bg-green-600 h-8 w-8 flex items-center justify-center text-white rounded-full">
                           {profile.data?.name ? (
                              profile.data.name.match(/\b\w/g)?.join('').toUpperCase()
                           ) : (
                              <FontAwesomeIcon icon={faUser} />
                           )}
                        </div>
                        <div className="text-left leading-tight font-semibold">
                           {userName}
                           {base.account && <div className="text-xs opacity-70">{base.account.name}</div>}
                        </div>
                     </OverlayTrigger.Trigger>
                     <OverlayTrigger.Overlay className="origin-top-right absolute right-0 -mt-1 w-64 shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {(profile.data?.accounts?.length ?? 0) > 1 && (
                           <>
                              <div className="relative">
                                 {!query && (
                                    <FontAwesomeIcon
                                       icon={faSearch}
                                       className="absolute top-4 left-4 ml-0.5 text-gray-200"
                                    />
                                 )}
                                 <input
                                    ref={inputSearch}
                                    type="text"
                                    className="w-full px-3.5 py-3 outline-none text-sm text-gray-700"
                                    autoComplete="off"
                                    autoFocus
                                    value={query}
                                    onChange={onChange}
                                 />
                              </div>
                              <hr />
                              <div className="max-w-[400px] max-h-[400px] overflow-y-auto ">
                                 {query && (!filteredAccounts || filteredAccounts.length === 0) && (
                                    <div className="block p-4 text-sm text-gray-400 w-full text-left">
                                       No se encontraron resultados con el criterio de búsqueda
                                    </div>
                                 )}
                                 {filteredAccounts?.map((acc) => (
                                    <button
                                       key={acc.code}
                                       type="button"
                                       className="block p-4 text-sm text-gray-700 hover:bg-gray-100 w-full text-left text-ellipsis"
                                       onClick={() => handleOnClick(acc)}
                                    >
                                       {acc.name}
                                    </button>
                                 ))}
                              </div>
                              <hr />
                           </>
                        )}
                        <button
                           type="button"
                           className="block p-4 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                           onClick={logOut}
                        >
                           <FontAwesomeIcon icon={faSignOut} className="mr-2" />
                           Logout
                        </button>
                     </OverlayTrigger.Overlay>
                  </OverlayTrigger>
               </div>
            </>
         ) : (
            <div className="px-4 sm:px-6 lg:px-8">
               <Logo />
            </div>
         )}
      </header>
   )
}

export default Header
